import type { ThemeV2, TypographyStyle } from '@modules/themes/types/themesV2.types';

export interface StabilizeGeneratedThemeV2ThemeResult {
  theme: ThemeV2;
  warnings: string[];
}

const MIN_TITLE_FONT_SIZE = 18;
const MIN_SUBTITLE_FONT_SIZE = 12;
const MIN_OPTION_TITLE_FONT_SIZE = 15;
const MIN_OPTION_SUBTITLE_FONT_SIZE = 13;
const MIN_PROGRESS_HEIGHT = 4;

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function addWarning(warnings: string[], path: string, previous: unknown, next: unknown): void {
  warnings.push(`Stabilized ${path} from ${JSON.stringify(previous)} to ${JSON.stringify(next)} for Theme V2 preview usability.`);
}

function ensureFontSize(
  style: TypographyStyle | undefined,
  path: string,
  minimum: number,
  warnings: string[]
): void {
  if (!style) return;
  if (!isFiniteNumber(style.fontSize) || style.fontSize >= minimum) return;
  const previous = style.fontSize;
  style.fontSize = minimum;
  addWarning(warnings, `${path}.fontSize`, previous, minimum);
}

function ensureFontDefaults(
  style: TypographyStyle | undefined,
  path: string,
  defaults: {
    fontFamily?: TypographyStyle['fontFamily'];
    fontWeight?: TypographyStyle['fontWeight'];
  },
  warnings: string[]
): void {
  if (!style) return;
  if (!style.fontFamily && defaults.fontFamily) {
    style.fontFamily = defaults.fontFamily;
    addWarning(warnings, `${path}.fontFamily`, undefined, defaults.fontFamily);
  }
  if (style.fontWeight === undefined && defaults.fontWeight !== undefined) {
    style.fontWeight = defaults.fontWeight;
    addWarning(warnings, `${path}.fontWeight`, undefined, defaults.fontWeight);
  }
}

function ensureNumberMin(
  target: Record<string, unknown> | undefined,
  key: string,
  path: string,
  minimum: number,
  warnings: string[]
): void {
  if (!target) return;
  const value = target[key];
  if (!isFiniteNumber(value) || value >= minimum) return;
  target[key] = minimum;
  addWarning(warnings, path, value, minimum);
}

function withWarnings(theme: ThemeV2, warnings: string[]): ThemeV2 {
  if (warnings.length === 0) return theme;
  const existingWarnings = theme.generatedMetadata?.warnings ?? [];
  return {
    ...theme,
    generatedMetadata: {
      ...theme.generatedMetadata,
      sourceKind: theme.generatedMetadata?.sourceKind ?? 'image-analysis',
      warnings: Array.from(new Set([...existingWarnings, ...warnings])),
    },
  };
}

/**
 * Stabilize high-risk model-derived Theme V2 values before preview/apply.
 *
 * Image analysis often returns crop-local text sizes from montage screenshots.
 * Colors and radii are usually safe to propagate directly, while very small
 * typography/progress values can make screens unreadable. This pass keeps the
 * visual identity but floors known fragile tokens to app-usable values.
 */
export function stabilizeGeneratedThemeV2Theme(theme: ThemeV2): StabilizeGeneratedThemeV2ThemeResult {
  const next = cloneJson(theme);
  const warnings: string[] = [];
  const mainFontFamily = next.fonts?.main?.fontFamily;

  ensureFontSize(next.fonts?.title, 'fonts.title', MIN_TITLE_FONT_SIZE, warnings);
  ensureFontSize(next.fonts?.subtitle, 'fonts.subtitle', MIN_SUBTITLE_FONT_SIZE, warnings);
  ensureFontSize(next.optionsListSettings?.itemTitle, 'optionsListSettings.itemTitle', MIN_OPTION_TITLE_FONT_SIZE, warnings);
  ensureFontSize(next.optionsListSettings?.itemSubtitle, 'optionsListSettings.itemSubtitle', MIN_OPTION_SUBTITLE_FONT_SIZE, warnings);

  ensureFontDefaults(next.fonts?.title, 'fonts.title', { fontFamily: mainFontFamily, fontWeight: 700 }, warnings);
  ensureFontDefaults(next.fonts?.subtitle, 'fonts.subtitle', { fontFamily: mainFontFamily, fontWeight: 500 }, warnings);
  ensureFontDefaults(next.optionsListSettings?.itemTitle, 'optionsListSettings.itemTitle', { fontFamily: mainFontFamily, fontWeight: 600 }, warnings);
  ensureFontDefaults(next.optionsListSettings?.itemSubtitle, 'optionsListSettings.itemSubtitle', { fontFamily: mainFontFamily, fontWeight: 400 }, warnings);

  ensureNumberMin(
    next.layoutSettings?.progress as Record<string, unknown> | undefined,
    'indicatorHeight',
    'layoutSettings.progress.indicatorHeight',
    MIN_PROGRESS_HEIGHT,
    warnings
  );
  ensureNumberMin(
    next.layoutSettings?.progress as Record<string, unknown> | undefined,
    'trackHeight',
    'layoutSettings.progress.trackHeight',
    MIN_PROGRESS_HEIGHT,
    warnings
  );

  return {
    theme: withWarnings(next, warnings),
    warnings,
  };
}
